/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect } from 'react';
import { Dock } from '../../../pages/onboarding/dock/dock';
import { TypeDock, AttachedFile } from '../../../pages/onboarding/type-dock/type-dock';
import { RecordDock } from '../../../pages/onboarding/record-dock/record-dock';
import styled from '@emotion/styled';
import { useKeyboardHeight } from '../../../hooks/use-keyboard-height';

interface AnimatedDockContainerProps {
  // Default dock props
  defaultVariant?: 'default' | 'question' | 'done';
  onTypeClick?: () => void;
  onRecordClick?: () => void;

  // Type dock props
  onTypeDockClose?: (text?: string) => void;
  onTypeSubmit?: (text: string, files?: File[]) => void;
  typeInitialText?: string;

  // Record dock props
  onRecordDockClose?: () => void;
  onRecordSend?: (audioBlob: Blob, files?: File[], transcript?: string) => void;

  // Done dock props
  onDoneClick?: () => void;

  // Current question context
  currentQuestionConversationId?: string;
  isCompleted?: boolean;

  // External control
  resetToDefault?: boolean;
  onDockStateChange?: (dockType: 'default' | 'type' | 'record') => void;

  // Submission state
  isSubmitting?: boolean;
  uploadProgress?: number | null;
}

const AnimatedDockWrapper = styled.div<{
  maxHeight: string;
  isAnimating: boolean;
  currentDock?: 'default' | 'type' | 'record';
  isKeyboardOpen?: boolean;
}>`
  max-height: ${(props) => props.maxHeight};
  overflow: hidden;
  transition: max-height 0.4s ease-in-out;
  margin-bottom: calc(env(safe-area-inset-bottom, 0px) + 10px);
  width: 100%;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  flex: none;
`;

export const AnimatedDockContainer: React.FC<AnimatedDockContainerProps> = ({
  defaultVariant = 'question',
  onTypeClick,
  onRecordClick,
  onTypeDockClose,
  onTypeSubmit,
  typeInitialText = '',
  // onRecordDockClose,
  onRecordSend,
  onDoneClick,
  isCompleted = false,
  resetToDefault = false,
  onDockStateChange,
  currentQuestionConversationId,
  isSubmitting = false,
  uploadProgress,
}) => {
  const [currentDock, setCurrentDock] = useState<'default' | 'type' | 'record'>('default');
  const [isAnimating, setIsAnimating] = useState(false);
  const [maxHeight, setMaxHeight] = useState('600px');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [draftText, setDraftText] = useState('');
  const { open: isKeyboardOpen } = useKeyboardHeight();
  const dockRef = useRef<HTMLDivElement>(null);

  // Handle external reset
  useEffect(() => {
    if (resetToDefault) {
      setCurrentDock('default');
    }
  }, [resetToDefault]);

  // Clear files and drafts when question changes
  useEffect(() => {
    setAttachedFiles([]);
    setDraftText('');
  }, [currentQuestionConversationId]);

  // Notify parent of dock state changes
  useEffect(() => {
    onDockStateChange?.(currentDock);
  }, [currentDock]);

  const animateDockSwitch = (newDock: 'default' | 'type' | 'record') => {
    if (isAnimating || currentDock === newDock) return;

    setIsAnimating(true);
    setMaxHeight('0px');

    // Wait for collapse animation to complete, then switch dock and expand
    setTimeout(() => {
      setCurrentDock(newDock);
      setMaxHeight('600px');

      // Wait for expand animation to complete
      setTimeout(() => {
        setIsAnimating(false);
      }, 400);
    }, 400);
  };

  const handleTypeClick = () => {
    animateDockSwitch('type');
    onTypeClick?.();
  };

  const handleRecordClick = () => {
    animateDockSwitch('record');
    onRecordClick?.();
  };

  const handleTypeDockClose = (text?: string) => {
    if (text !== undefined) {
      setDraftText(text);
    }
    animateDockSwitch('default');
    onTypeDockClose?.();
  };

  const handleRecordDockClose = () => {
    animateDockSwitch('default');
    // onRecordDockClose?.();
  };

  const handleTypeSubmit = (text: string, files?: File[]) => {
    if (onTypeSubmit) {
      onTypeSubmit(text, files);
    }
  };

  const handleRecordSend = (audioBlob: Blob, files?: File[], transcript?: string) => {
    if (onRecordSend) {
      onRecordSend(audioBlob, files, transcript);
    }
    // Clear text draft when sending audio
    setDraftText('');
  };

  const handleTypeToRecordSwitch = () => {
    setCurrentDock('record');
  };

  const renderCurrentDock = () => {
    switch (currentDock) {
      case 'type':
        return (
          <TypeDock
            onClose={handleTypeDockClose}
            onSubmit={handleTypeSubmit}
            initialText={draftText || typeInitialText}
            onRecordClick={handleTypeToRecordSwitch}
            attachedFiles={attachedFiles}
            onFilesChange={setAttachedFiles}
            disabled={isSubmitting}
            uploadProgress={uploadProgress}
            isKeyboardOpen={isKeyboardOpen}
          />
        );

      case 'record':
        return (
          <RecordDock
            onClose={handleRecordDockClose}
            onSend={handleRecordSend}
            animateInOnMount={true}
          />
        );

      default:
        return (
          <Dock
            variant={isCompleted ? 'done' : defaultVariant}
            onTypeClick={handleTypeClick}
            onRecordClick={handleRecordClick}
            {...(onDoneClick !== undefined ? { onDoneClick } : {})}
            disabled={isSubmitting}
          />
        );
    }
  };

  return (
    <AnimatedDockWrapper currentDock={currentDock} maxHeight={maxHeight} isAnimating={isAnimating} isKeyboardOpen={isKeyboardOpen}>
      <div ref={dockRef}>{renderCurrentDock()}</div>
    </AnimatedDockWrapper>
  );
};
